import React, {forwardRef, useEffect, useImperativeHandle, useState} from "react";
import {UploadStates} from "../../../../core/constants/enums";
import ManageTagIconDropzone from "../dropzone/manage-tag-icon";
import useIsMounted from "../../../hooks/use-is-mounted";
import Api from "../../../../core/services/api/api";

const ManageTagIcon = forwardRef(({tagId, tagUrl, updateTagIconUrl, preventAutoCommit = false, onChangesMade}, ref) => {
    const [uploadState, setUploadState] = useState({});
    const [url, setUrl] = useState(tagUrl);
    const [uncommittedAction, setUnCommittedAction] = useState({action: null, args: []});
    const [removing, setRemoving] = useState(false);
    const isMounted = useIsMounted();

    /**
     * Exposes the commit logic of the manage tag icon component to the parent component.
     */
    useImperativeHandle(ref, () => ({
        /**
         * Commits the uncommitted action of the changes made in the dropzone.
         * @return {any}
         */
        commit: () => commitChanges(),
    }), [uncommittedAction, preventAutoCommit])

    /**
     * With each change in the tagUrl:
     * - syncs the url of the state with the given tagUrl.
     */
    useEffect(() => {
        setUrl(tagUrl);
    }, [tagUrl])

    /**
     * With each change in the action of the uncommittedAction state:
     * - invokes the callback to indicate whether there exists any changes made in the dropzone.
     */
    useEffect(() => {
        if (!preventAutoCommit) {
            onChangesMade && onChangesMade(false);
            return
        }
        onChangesMade && onChangesMade(!!uncommittedAction?.action);
    }, [uncommittedAction?.action])

    /**
     * Tracks the progress of the uploading tag icon, and updates the status based on it.
     * @param progressEvent {ProgressEvent}
     */
    const onUploadProgress = (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadState({
            state: UploadStates.pending,
            percentage: percentCompleted
        });
    }

    /**
     * Uploads a new version of the tag icon in the server with the given iconInfo.
     *
     * * if the response of the api is successful, calls updateTagIconUrl with api response data to indicate the change.
     * @param iconInfo
     * @return {Promise<boolean | undefined>}
     */
    const uploadTagIcon = async (iconInfo) => {
        const forApi = {
            id: tagId,
            icon: iconInfo.contentBytes,
        }
        setUploadState({state: UploadStates.pending, percentage: 0});
        const response = await Api.updateTagInformation(forApi, onUploadProgress);
        if (!isMounted())
            return false;
        if (response?.resultFlag) {
            setUploadState({state: UploadStates.done, percentage: 0});
            const newFileName = (response?.configuration?.TagIconUrlBaseAddress ?? '') + response?.data?.iconFileName;
            setUrl(newFileName)
            updateTagIconUrl(newFileName);
        } else {
            setUploadState({state: UploadStates.error, percentage: 0});
        }
        return response?.resultFlag;
    }

    /**
     * Removes the current icon of the given tag in the server.
     *
     * * if the result of the api is successful, calls updateTagIconUrl with value of null to indicate the removal
     * @return {Promise<boolean | undefined>}
     */
    const removeTagIcon = async () => {
        setRemoving(true);
        const forApi = {
            id: tagId,
            deleteIcon: true,
        }
        const response = await Api.updateTagInformation(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setUrl(null)
            updateTagIconUrl(null);
        }
        setRemoving(false);
        return response?.resultFlag;
    }

    /**
     * Determines if the changes made to the url should automatically be committed in the server.
     *
     * * if [preventAutoCommit] then saves the uncommitted changes in the state for calls of [commit] through parent
     * component and also sets the url to reflect temporary changes.
     * @param action
     * @param args
     * @return {Promise<boolean | undefined>|void}
     */
    const determineAutoCommitBehaviour = (action, ...args) => {
        if (preventAutoCommit) {
            setUnCommittedAction({action: action, args: args});
            switch (action) {
                case 'upload':
                    return setUrl(args[0].dataUrl);
                case 'remove':
                    return setUrl(null);
                default:
                    return;
            }
        }
        switch (action) {
            case 'upload':
                return uploadTagIcon(...args).then();
            case 'remove':
                return removeTagIcon().then();
            default:
                return;
        }
    }

    /**
     * Commits the uncommitted action of the changes made in the dropzone.
     * @return {any}
     */
    const commitChanges = () => {
        if (!preventAutoCommit || !uncommittedAction?.action)
            return;
        switch (uncommittedAction.action) {
            case 'upload':
                return uploadTagIcon(...uncommittedAction.args).then();
            case 'remove':
                return removeTagIcon().then();
            default:
                return;
        }
    }

    return (
        <>
            <ManageTagIconDropzone
                upload={(...args) => determineAutoCommitBehaviour('upload', ...args)}
                uploadState={uploadState}
                remove={() => determineAutoCommitBehaviour('remove')}
                removing={removing}
                url={url}
            />
        </>
    )
})

export default ManageTagIcon;
